/**
 * @fileOverview Displays a combination of filter options and a search box
 * horizontally. Designed to be used on the filter table.
 */

import React, { useEffect, useState } from 'react'
import moment from 'moment'
import classNames from 'classnames'
import injectStyles from 'react-jss'

import ButtonGroup from '@material-ui/core/ButtonGroup'
import SubmitButton from 'Components/Form/SubmitButton'
import SearchBox from './SearchBox'
import { FilterSelect } from './../Filter/FilterSelect'
import './types'
import { urlGet, urlSet, urlRemove } from './../../Utils/URL'
import { isDefined } from '../../shared/libraries/utils/object'
import DateRangeSelector from '../DateRangeSelector'
import { splitTitle } from './utils'
import { styles } from './styles'

/**
 * @typedef {SearchAndFiltersProps}
 * @property {Array<Filter|MultiFilter>} filters The filter options, see the
 * types file.
 * @property {string} searchText The current search text
 * @property {Function} onSearchTextChange Fired when the search is triggerd.
 * i.e the user hits enter or clicks the search button. If you want finer
 * grained control on changing on keyPress use the `searchHandleInputChange`
 * method.
 * @property {string} className Override the root class name.
 * @property {string} searchInputClassName Override the search input class name.
 * @property {Function} searchHandleInputChange Fired on each key press / change
 * for the search input field, useful if you want to customise your own
 * auto complete options.
 * @property {boolean} enableFilterSuggestions If set to true, makes every
 * filter dropdown option something that can be searched for and tagged to
 * specify. Defaults to false. May want to use this in combination with
 * `enableMultiFilterValues`.
 * @property {boolean} enableMultiFilterValues Defaults to false. If set to
 * true you must provide filters of type MultiFilter. This will allow the user
 * to apply more than one option to a filter at a time. The gotcha with this
 * is that once enabled the filter `onChange` event's first parameter will
 * be an array and not a string.
 * @property {Array<string>} searchTags Add your own selected search tags.
 * @property {Array<string>} searchSuggestions Provide your own auto complete /
 * search suggestions.
 * @property {Function} searchOnRemoveTag If providing your own tags this will
 * fire when a tag is removed, the only parameter is the string tag.
 * @property {Function} searchOnAddTag If providing your own suggestions this
 * will fire when a suggestion is selected, the only parameter is the string
 * suggestion.
 *
 * @param {SearchAndFiltersProps} props
 */
const SearchAndFilters = props => {
  var {
    onSearchTextChange,
    searchText,
    filters = [],
    classes,
    className,
    searchInputClassName,
    searchHandleInputChange,
    enableFilterSuggestions,
    enableMultiFilterValues,
    searchTags,
    searchSuggestions,
    searchOnRemoveTag,
    searchOnAddTag,
    presets,
  } = props

  var showSearch = onSearchTextChange || searchText !== undefined
  const [filtersPreset, setFiltersPreset] = useState('No Preset')

  const dropPresets = () => {
    if (filtersPreset !== 'No Preset') {
      setFiltersPreset('No Preset')
    }
  }

  useEffect(
    () => {
      filters.forEach(({ label, onChange }) => {
        var value = urlGet(label)
        if (value) onChange(value)
      })
      if (showSearch) {
        var value = urlGet('search')
        if (value) onSearchTextChange(value)
      }
    },
    // Run only once, as this is to sync on load only
    [],
  )

  var localTags = [],
    localSuggestions = []

  if (enableFilterSuggestions) {
    let suggestionsHash = {}
    filters.forEach(({ values }) => {
      values.forEach(v => (suggestionsHash[v] = true))
    })
    var filterTags = filters.map(({ selectedValue }) => {
      if (selectedValue == null) return undefined
      if (!(selectedValue instanceof Array)) selectedValue = [selectedValue]
      selectedValue.forEach(v => delete suggestionsHash[v])
      return selectedValue
    })
    filterTags = filterTags.flat()
    filterTags.forEach(t => {
      if (t) localTags.push(t)
    })
    var filterSuggestions = Object.keys(suggestionsHash)
    filterSuggestions.forEach(s => localSuggestions.push(s))
    var onAddTag = function(tag) {
      // Find the first matching filter option
      for (var i in filters) {
        var { values, onChange, selectedValue } = filters[i]
        for (var j in values) {
          var v = values[j]
          if (v == tag) {
            if (enableMultiFilterValues) {
              if (selectedValue == null) var selectedValues = []
              else var selectedValues = selectedValue.slice(0)
              selectedValues.push(tag)
              return onChange(selectedValues)
            } else {
              return onChange(tag)
            }
          }
        }
      }
      if (searchOnAddTag) return searchOnAddTag(tag)
    }
    var onRemoveTag = function(tag) {
      dropPresets()
      var handled = filters.find(({ label, selectedValue, onChange }) => {
        if (selectedValue instanceof Array) {
          var index = selectedValue.findIndex(v => v == tag)
          if (index != -1) {
            var newSelectedValues = selectedValue.slice(0)
            newSelectedValues.splice(index, 1)
            onChange(newSelectedValues.length == 0 ? null : newSelectedValues)
            return true
          }
          return undefined
        }
        if (selectedValue == tag) {
          onChange(null)
          urlRemove(label)
          return true
        }
        return undefined
      })
      if (!handled && searchOnRemoveTag) return searchOnRemoveTag(tag)
    }
  }

  if (searchTags) {
    searchTags.forEach(t => localTags.push(t))
  }

  if (searchSuggestions) {
    searchSuggestions.forEach(s => localSuggestions.push(s))
  }

  return (
    <div
      className={classNames(
        {
          [classes.container]: true,
        },
        className,
        'SearchAndFilters',
      )}
    >
      {filters.length
        ? filters.map(filter => {
            // We want to add an 'All' filter option
            var values = filter.values.slice(0)
            values.splice(0, 0, 'All')

            var options = values.map(v => ({ label: v, value: v }))

            // If the 'All' option is selected then return null as the selection
            var onChange = (e, i, v) => {
              dropPresets()
              v = v == 'All' ? null : v
              if (enableMultiFilterValues) {
                // The current UI only allows you select one item at a time
                return filter.onChange(v == null ? null : [v])
              }
              return filter.onChange(v)
            }
            const onDateChange = value => {
              if (!value) {
                urlRemove(filter.label)
                return filter.onChange('')
              }
              const date = moment(value).format('YYYY-MM-DDTHH:mm:ssZ')
              urlSet(filter.label, date)
              return filter.onChange(date)
            }
            var selectedValue =
              filter.selectedValue == null ? 'All' : filter.selectedValue
            if (
              selectedValue instanceof Array &&
              selectedValue.length == 1 &&
              selectedValue[0] == null
            ) {
              throw new Error(
                "Don't pass [null], instead set selectedValue to null.",
              )
            }

            if (selectedValue instanceof Array) {
              if (selectedValue.length > 1) {
                options = ['All', 'Multiple selected'].map(v => ({
                  label: v,
                  value: v,
                }))
                selectedValue = 'Multiple selected'
                var disabled = true
              } else {
                selectedValue = selectedValue[0]
              }
            }
            if (filter.label != 'startDate' && filter.label != 'endDate') {
              return (
                <div className="SearchAndFilters-filterGroup">
                  <FilterSelect
                    label={filter.label}
                    onChange={onChange}
                    options={options}
                    value={selectedValue}
                    disabled={disabled}
                    sorted={isDefined(filter.sorted) ? filter.sorted : true}
                  />
                </div>
              )
            }
            return (
              <div className="SearchAndFilters-filterGroup">
                <DateRangeSelector
                  title={splitTitle(filter.label)}
                  value={selectedValue ? moment(selectedValue) : ''}
                  onChange={value => {
                    onDateChange(value)
                    dropPresets()
                  }}
                />
              </div>
            )
          })
        : null}
      {showSearch ? (
        <div className={classes.searchContainer}>
          <SearchBox
            inputClassName={classNames(classes.searchBox, searchInputClassName)}
            onChange={str => {
              if (str) {
                setFiltersPreset('No Preset')
                urlSet('search', str)
              }
              // Label could conflict with a filter, but seems unlikely
              else urlRemove('search')
              return onSearchTextChange(str)
            }}
            value={searchText}
            handleInputChange={searchHandleInputChange}
            suggestions={localSuggestions}
            tags={localTags}
            onAddTag={onAddTag}
            onRemoveTag={onRemoveTag}
          />
          {presets && presets.length
            ? presets.map(preset => (
                <div className={classes.presets}>
                  <ButtonGroup
                    disableElevation
                    color="primary"
                    className={classes.select}
                  >
                    {preset.items.map(item => (
                      <SubmitButton
                        onClick={() => {
                          return (
                            setFiltersPreset(item.label), item.action(filters)
                          )
                        }}
                        value={item.label}
                        variant={
                          filtersPreset === item.label
                            ? 'contained'
                            : 'outlined'
                        }
                      />
                    ))}
                  </ButtonGroup>
                </div>
              ))
            : null}
        </div>
      ) : null}
    </div>
  )
}

const StyledSearchAndFilters = injectStyles(styles)(SearchAndFilters)

export default StyledSearchAndFilters



// WEBPACK FOOTER //
// ./src/Components/FilterAndPaginationComponent/SearchAndFilters.js